import { useApolloClient } from "@apollo/client";
import { Box, Button, Divider, FormControlLabel, Grid, Switch, Typography } from "@mui/material";
import { useNutritionFlags } from "apps/web/src/hooks/useNutritionFlags";
import {
  CurrentRecipesForOldRecipesDocument,
  CurrentRecipesForOldRecipesQuery,
  CurrentRecipesForOldRecipesQueryVariables,
  FullServingAmountFragment,
  RecipeFullFragment,
  RecipeLimitedAccessFragment,
  RecipeSearchFragment,
  useMyScoringSystemQuery,
} from "apps/web/src/types";
import { useHasIntegration } from "apps/web/src/utils/integration";
import { useEffect, useRef, useState } from "react";
import { ServingAmountViewChipList } from "shared-ui/ServingAmount/View/ChipList";
import { newServingAmount, newServingAmountWithAmount, servingAmountsUseOldRecipe } from "shared-ui/ServingAmount/utils";
import { getNextPosition } from "shared-ui/utils/getNextPosition";
import { useIsFoodFormRedesign } from "../../FoodFormUi/FoodFormUiContext";
import CompassImportRecipeDialog from "../../Integration/Compass/ImportRecipeDialog";
import { RecipeAssistedMethodButtons } from "../../Recipe/Form/RecipeAssistedMethodButtons";
import { RecipeAssistedStartCards } from "../../Recipe/Form/RecipeAssistedStartCards";
import { getRecipeAssistedEntryMode } from "../../Recipe/Form/recipeAssistedEntry";
import RecipeCreateDialog from "../../Recipe/RecipeCreateDialog";
import { ServingAmountsEditChipListWithSearchBar } from "../../ServingAmounts/Edit/ChipListWithSearchBar";
import { useServingAmountModeContext } from "../../ServingAmounts/contexts/ServingAmountMode";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import WarningIcon from "../../universal/WarningIcon";
import { MenuItemChangeScoreAction, MenuItemChangeServingAmountsAction } from "../reducer";
import { ScoreOverrideDialog } from "./ScoreOverrideDialog";

interface IngredientsProps {
  itemName: string;
  servingAmounts: readonly FullServingAmountFragment[];
  onChangeServingAmounts: (args: MenuItemChangeServingAmountsAction["payload"]) => void;
  onChangeScore: (args: MenuItemChangeScoreAction["payload"]) => void;
  onAiGenerateClicked?: null | (() => void);
  onAiTranscribeClicked?: null | (() => void);
  onImportFromLinkClicked?: () => void;
  hasIngredients?: boolean;
  allowSaveAsRecipe?: boolean;
  displayMacros?: boolean;
  onChangeDisplayMacros?: (displayMacros: boolean) => void;
}

export const Ingredients = ({
  itemName,
  servingAmounts,
  onChangeServingAmounts,
  onChangeScore,
  onAiGenerateClicked,
  onAiTranscribeClicked,
  onImportFromLinkClicked,
  hasIngredients: _hasIngredients,
  allowSaveAsRecipe = true,
  displayMacros: displayMacrosProp,
  onChangeDisplayMacros,
}: IngredientsProps) => {
  const isRedesign = useIsFoodFormRedesign();
  const { isGetRecipeFromUrlEnabled } = useNutritionFlags();
  const [saveAsRecipeModalOpen, setSaveAsRecipeModalOpen] = useState(false);
  const [compassDialogOpen, setCompassDialogOpen] = useState(false);
  const [scoreOverrideDialogOpen, setScoreOverrideDialogOpen] = useState(false);
  const [localDisplayMacros, setLocalDisplayMacros] = useState(true);
  const displayMacros = displayMacrosProp ?? localDisplayMacros;
  const handleChangeDisplayMacros = onChangeDisplayMacros ?? setLocalDisplayMacros;

  const servingAmountMode = useServingAmountModeContext();
  const forRestaurant = servingAmountMode.type === "restaurant";
  const showSaveAsRecipe = allowSaveAsRecipe && servingAmounts.length > 1 && !forRestaurant;

  const hasCompassIntegration = useHasIntegration("compass");
  const showCompassImport = hasCompassIntegration && !forRestaurant;

  const [scoredIngredientRecipe, setScoredIngredientRecipe] = useState<RecipeLimitedAccessFragment | null>(null);

  const { data: scoringSystemData, loading: loadingScoringSystem } = useMyScoringSystemQuery();
  const orgScoringSystem = scoringSystemData?.myScoringSystem;

  const apolloClient = useApolloClient();
  const { setMessage } = useSnackbar();
  const [updatingToLatestRecipes, setUpdatingToLatestRecipes] = useState(false);
  const servingAmountsRef = useRef(servingAmounts);
  useEffect(() => {
    servingAmountsRef.current = servingAmounts;
  }, [servingAmounts]);

  const handleUpdateToLatestRecipes = async () => {
    const outdatedRecipeIds = Array.from(
      new Set(
        servingAmounts
          .filter(sa => sa.serving.foodOrRecipe.__typename === "Recipe" && !sa.serving.foodOrRecipe.isCurrent)
          .map(sa => sa.serving.foodOrRecipe.id)
      )
    );
    if (outdatedRecipeIds.length === 0) {
      return;
    }
    setUpdatingToLatestRecipes(true);
    try {
      const { data } = await apolloClient.query<CurrentRecipesForOldRecipesQuery, CurrentRecipesForOldRecipesQueryVariables>({
        query: CurrentRecipesForOldRecipesDocument,
        variables: { ids: outdatedRecipeIds },
        fetchPolicy: "network-only",
      });
      const currentByOldId = new Map(data.recipesById.map(r => [r.id, r.history.current]));
      const updated = servingAmountsRef.current.map(sa => {
        if (sa.serving.foodOrRecipe.__typename !== "Recipe" || sa.serving.foodOrRecipe.isCurrent) {
          return sa;
        }
        const current = currentByOldId.get(sa.serving.foodOrRecipe.id);
        if (!current || current.servings.length === 0) {
          return sa;
        }
        const defaultServing = current.servings.find(s => s.isDefault) ?? current.servings[0];
        return newServingAmountWithAmount(defaultServing, sa.position, sa.amount);
      });
      onChangeServingAmounts({ servingAmounts: updated });
    } catch {
      setMessage("error", "Unable to update ingredients to the latest recipe versions");
    } finally {
      setUpdatingToLatestRecipes(false);
    }
  };

  const handleSaveAsRecipe = (recipe: RecipeFullFragment) => {
    const serving = recipe.servings[0];
    const newServingAmounts = [
      newServingAmount(
        {
          ...serving,
          foodOrRecipe: recipe,
        },
        1
      ),
    ];
    onChangeServingAmounts({ servingAmounts: newServingAmounts });
  };
  const handleRecipeImport = (recipe: RecipeSearchFragment) => {
    if (recipe.servings.length === 0) {
      return;
    }
    const serving = recipe.servings.find(s => s.isDefault) || recipe.servings[0];
    const servingAmount = newServingAmount({ ...serving, foodOrRecipe: recipe }, getNextPosition(servingAmounts));
    onChangeServingAmounts({ servingAmounts: [...servingAmounts, servingAmount] });
  };

  const showLink = Boolean(isGetRecipeFromUrlEnabled && onImportFromLinkClicked);
  const hasAssistedMethods = Boolean(onAiGenerateClicked || onAiTranscribeClicked || showLink);
  const entryMode = getRecipeAssistedEntryMode(servingAmounts.length);
  const showStartCards = hasAssistedMethods && entryMode === "cards";
  const showCompactMethods = hasAssistedMethods && entryMode === "compact";
  const assistedHandlers = {
    showLink,
    onImportFromLink: showLink ? onImportFromLinkClicked : undefined,
    onTranscribePhoto: onAiTranscribeClicked ?? undefined,
    onGenerateWithAi: onAiGenerateClicked ?? undefined,
  };

  const actionButtons = (
    <>
      {showCompassImport && (
        <Button fullWidth variant="outlined" onClick={() => setCompassDialogOpen(true)}>
          Import Compass Recipe
        </Button>
      )}
      {isRedesign && showSaveAsRecipe && (
        <Button fullWidth variant="outlined" onClick={() => setSaveAsRecipeModalOpen(true)}>
          Save as Recipe
        </Button>
      )}
    </>
  );

  return (
    <Box sx={isRedesign ? { display: "flex", flexDirection: "column", gap: 2 } : undefined}>
      <IngredientsHeader
        servingAmounts={servingAmounts}
        showWarning={true}
        onUpdateToLatest={handleUpdateToLatestRecipes}
        updatingToLatest={updatingToLatestRecipes}
        displayMacros={displayMacros}
        onChangeDisplayMacros={isRedesign ? handleChangeDisplayMacros : undefined}
      />
      {showStartCards && (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="body1Semibold">Start your ingredient list</Typography>
            <Typography variant="body2" color="text.secondary">
              Bring a recipe in from somewhere else, or add ingredients one at a time. Everything stays editable.
            </Typography>
          </Box>
          <RecipeAssistedStartCards {...assistedHandlers} />
          <Divider
            sx={{
              my: 0.5,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "text.secondary",
              "&::before, &::after": {
                borderColor: "greyscale.300",
              },
            }}
          >
            or add ingredients yourself
          </Divider>
        </>
      )}
      <ServingAmountsEditChipListWithSearchBar
        enableBranded
        includeRecipeIngredients
        displayMacros={displayMacros}
        onChange={e => {
          const scoredRecipe = getScoredRecipe(e);
          if (scoredRecipe !== null) {
            setScoredIngredientRecipe(scoredRecipe);
            setScoreOverrideDialogOpen(true);
          } else {
            setScoredIngredientRecipe(null);
          }

          onChangeServingAmounts({ servingAmounts: e });
        }}
        selected
        selectedServingAmounts={servingAmounts}
        toolbarSlot={showCompactMethods ? <RecipeAssistedMethodButtons {...assistedHandlers} /> : undefined}
      />
      {!isRedesign && showSaveAsRecipe && (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Button size="small" variant="text" sx={{ justifyContent: "flex-end", pt: 1 }} onClick={() => setSaveAsRecipeModalOpen(true)}>
            Save as Recipe
          </Button>
        </Box>
      )}
      {(showCompassImport || (isRedesign && showSaveAsRecipe)) &&
        (isRedesign ? (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>{actionButtons}</Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>{actionButtons}</Box>
        ))}
      {saveAsRecipeModalOpen && (
        <RecipeCreateDialog
          onCreate={handleSaveAsRecipe}
          open={saveAsRecipeModalOpen}
          onClose={() => setSaveAsRecipeModalOpen(false)}
          initialIngredients={servingAmounts}
          initialName={itemName}
        />
      )}
      {compassDialogOpen && (
        <CompassImportRecipeDialog open={compassDialogOpen} onClose={() => setCompassDialogOpen(false)} onImport={handleRecipeImport} />
      )}
      {!loadingScoringSystem && (
        <ScoreOverrideDialog
          open={scoreOverrideDialogOpen}
          onConfirm={() => {
            onChangeScore({ scoreValue: scoredIngredientRecipe?.score?.value || null, scoringSystem: orgScoringSystem || null });
            setScoreOverrideDialogOpen(false);
          }}
          onClose={() => {
            setScoreOverrideDialogOpen(false);
          }}
        />
      )}
    </Box>
  );
};

const getScoredRecipe = (servingAmounts: readonly FullServingAmountFragment[]) => {
  const recipes: RecipeLimitedAccessFragment[] = servingAmounts
    .map(s => s.serving.foodOrRecipe)
    .filter((f): f is RecipeLimitedAccessFragment => f.__typename === "Recipe");
  const scoredRecipes = recipes.filter(r => r.score !== null);
  return scoredRecipes.length > 0 ? scoredRecipes[0] : null;
};

interface ReadonlyIngredientsProps {
  servingAmounts: readonly FullServingAmountFragment[];
}

export const ReadonlyIngredients = ({ servingAmounts }: ReadonlyIngredientsProps) => {
  return (
    <Grid container>
      <Grid item xs={12} sx={{ display: "inline" }}>
        <IngredientsHeader servingAmounts={servingAmounts} />
      </Grid>
      <Grid item xs={12}>
        <ServingAmountViewChipList servingAmounts={servingAmounts} />
      </Grid>
    </Grid>
  );
};

interface IngredientsHeaderProps {
  servingAmounts: readonly FullServingAmountFragment[];
  showWarning?: boolean;
  onUpdateToLatest?: () => void;
  updatingToLatest?: boolean;
  displayMacros?: boolean;
  onChangeDisplayMacros?: (value: boolean) => void;
}

const IngredientsHeader = ({
  servingAmounts,
  showWarning,
  onUpdateToLatest,
  updatingToLatest,
  displayMacros = true,
  onChangeDisplayMacros,
}: IngredientsHeaderProps) => {
  const isRedesign = useIsFoodFormRedesign();
  const hasOldRecipe = servingAmountsUseOldRecipe(servingAmounts);

  if (isRedesign) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="h4">Ingredients</Typography>
            <Typography variant="body2" color="textSecondary">
              (per portion)
            </Typography>
          </Box>
          {onChangeDisplayMacros && (
            <FormControlLabel
              label="Display macros"
              control={<Switch size="small" checked={displayMacros} onChange={(_, checked) => onChangeDisplayMacros(checked)} />}
              sx={{ m: 0, gap: 1 }}
            />
          )}
        </Box>
        {showWarning && hasOldRecipe && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <WarningIcon tip="Old versions of recipes are being used" />
            {onUpdateToLatest && (
              <Button size="small" variant="text" sx={{ ml: 0.5 }} onClick={onUpdateToLatest} disabled={updatingToLatest}>
                Update to latest versions
              </Button>
            )}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1, display: "flex", flexDirection: "row", alignItems: "center" }}>
      <Typography variant="h3" color="textSecondary">
        Ingredients
      </Typography>
      <Box sx={{ minWidth: 5 }} />
      <Typography color="textSecondary">(per portion)</Typography>
      {showWarning && hasOldRecipe && <WarningIcon tip="Old versions of recipes are being used" sx={{ pl: 1 }} />}
      {showWarning && hasOldRecipe && onUpdateToLatest && (
        <Button size="small" variant="text" sx={{ ml: 0.5 }} onClick={onUpdateToLatest} disabled={updatingToLatest}>
          Update to latest versions
        </Button>
      )}
    </Box>
  );
};
